import React, { useCallback, useEffect, useState } from "react";
import { XCircle } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { DocumentUploadCard } from "../ui/DocumentUploadCard";
import { UploadProgress } from "../ui/UploadProgress";
import { DocumentTypeSelector } from "../ui/DocumentTypeSelector";

import { InvalidReasonDisplay } from "../ui/InvalidReasonDisplay";
import { CancelToReview } from "../CancelToReview";
import { FormValue, Status } from "../../../redux/interfaces/folder.types";
import {
  getDocumentUploadPolicy,
  MEGABYTE,
} from "../../../constants/uploadPolicies";
import { DocumentExamples } from "./DocumentExamples";
import {
  proofOfAddressTypes,
  proofOfAddressTypesMap,
  labelProofOfAddressOptions,
} from "./fixtures/proofOfAddressTypes";
import { toast } from "sonner";
import { uploadFileRequirement } from "../../../utils/vault/uploadFileRequirement";
import { useInvalidateVaultFolder } from "../../../hooks/vault/queries/useVaultFolder";
import { useUploadFileToVault } from "../../../hooks/vault/mutations/useUploadFileToVault";
import { useUpdateVaultFormValues } from "../../../hooks/vault/mutations/useUpdateVaultFormValues";
import type { UploadFile } from "antd/es/upload/interface";
import { ProofOfAddressStatus } from "./ProofOfAddressStatus";
import {
  isProofOfAddressProcessing,
  isRequirementVisuallyComplete,
} from "../utils/requirementGrouping";

// Validation components
import {
  useProofOfAddressValidation,
  ProofOfAddressProcessingLoader,
  ProofOfAddressValidationLoader,
} from "./id-validation";

interface Props {
  type: "tenant" | "guarantor" | "company";
  onRegisterSubmit?: (submitFn: () => Promise<boolean>) => void;
  onSetNavigationHidden?: (hidden: boolean) => void;
  onProofOfAddressComplete?: (isComplete: boolean) => void;
  canContinue?: boolean;
}

export const ProofOfAddress: React.FC<Props> = ({
  type,
  onRegisterSubmit,
  onSetNavigationHidden,
  onProofOfAddressComplete,
  canContinue,
}) => {
  const [documentType, setDocumentType] = useState<string | undefined>(
    undefined,
  );
  const [files, setFiles] = useState<UploadFile[]>([]);
  const dispatch = useAppDispatch();
  const label = labelProofOfAddressOptions[type];

  const { activeRequirement, activeVault, activeTab } = useAppSelector(
    (state) => state.vault,
  );
  const uploadMutation = useUploadFileToVault(activeVault?.id);
  const updateFormValuesMutation = useUpdateVaultFormValues(activeVault?.id);
  const invalidateVaultFolder = useInvalidateVaultFolder(activeVault?.id);

  // OCR Validation flow hook (full state machine)
  const {
    isValidating,
    isRejected,
    progress: validationProgress,
    initializeFromRequirement,
    invalidReason,
    hasError,
    error,
  } = useProofOfAddressValidation();

  const enabledInputs =
    activeRequirement?.status === Status.PendingUpload ||
    activeRequirement?.status === Status.Invalid;
  const isProcessing = isProofOfAddressProcessing(activeRequirement);
  const isTerminal = isRequirementVisuallyComplete(activeRequirement);
  const maxFileSizeMB =
    getDocumentUploadPolicy({
      requirementSlug: activeRequirement?.slug ?? "proof-of-address",
      documentType,
      requirementStatus: activeRequirement?.status,
    }).max_file_bytes / MEGABYTE;

  const documentExamples = [
    {
      path: "/documents/comprobante_agua.jpg",
      description: "Comprobante del SIAPA",
    },
    {
      path: "/documents/comprobante-luz.webp",
      description: "Comprobante de la CFE",
    },
    {
      path: "/documents/comprobante_servicios.jpg",
      description: "Comprobante de servicios (Telmex)",
    },
  ];

  // Initialize state from requirement status AND workflow_step on load/change
  // workflow_step takes priority over status for more precise state restoration on reload
  useEffect(() => {
    if (activeRequirement) {
      initializeFromRequirement({
        id: activeRequirement.id,
        status: activeRequirement.status,
        invalid_reason: activeRequirement.invalid_reason,
        workflow_step: activeRequirement.workflow_step,
        verification_data: activeRequirement.verification_data,
      });
    }
  }, [activeRequirement, initializeFromRequirement]);

  const initFormValues = useCallback(() => {
    if (!activeRequirement?.form_values) return;

    const values = activeRequirement.form_values || [];
    const documentTypeValue =
      values.find((item: FormValue) => item.key === "document_type")?.value ||
      undefined;

    setDocumentType(documentTypeValue);
  }, [activeRequirement?.form_values]);

  useEffect(() => {
    setFiles([]);
    initFormValues();
  }, [activeRequirement?.id, initFormValues]);

  // Force a fresh re-upload after OCR rejection.
  // Without this, the previously submitted file remains selected in the upload card.
  useEffect(() => {
    if (isRejected || activeRequirement?.status === Status.Invalid) {
      setFiles([]);
    }
  }, [activeRequirement?.status, isRejected]);

  const handleDocumentTypeChange = (newType: string) => {
    if (newType !== documentType) {
      setFiles([]);
    }
    setDocumentType(newType);
  };

  // Persist first; Django queues verification from the immutable GCS object.
  const performUpload = useCallback(async (): Promise<boolean> => {
    if (!documentType) return false;

    const documentMap: Record<string, string> = {
      document_type: documentType,
    };

    const file = files[0]?.originFileObj;
    const formFiles = [file];
    const documentTypeKey = "proof_of_address";
    const requirementFiles = activeRequirement?.files || [];
    const formNFiles = 1;
    const successMessage = "Comprobante de domicilio subido correctamente.";

    const newFormValues: FormValue[] | undefined =
      activeRequirement?.form_values?.map((formItem) => ({
        ...formItem,
        value: documentMap[formItem.key],
      }));

    // Create a toast-based messageApi compatible object
    const messageApi = {
      success: (msg: string) => toast.success(msg),
      error: (msg: string) => toast.error(msg),
    };

    try {
      await uploadFileRequirement({
        uploadMutateAsync: uploadMutation.mutateAsync,
        updateFormValuesMutateAsync: updateFormValuesMutation.mutateAsync,
        dispatch,
        requirementFiles,
        documentType: documentTypeKey,
        uploadPolicyDocumentType: documentType,
        formNFiles,
        formFiles,
        formValues: {},
        activeRequirement,
        activeVault,
        activeTab,
        successMessage,
        reqFormValues: newFormValues,
        messageApi,
        status: "pending_review",
        skipAutoNavigation: true,
      });
      setFiles([]);
      return true;
    } catch (error) {
      console.error("Upload error:", error);
      return false;
    }
  }, [
    activeRequirement,
    activeTab,
    activeVault,
    dispatch,
    documentType,
    files,
    updateFormValuesMutation,
    uploadMutation,
  ]);

  // Upload first. Verification is asynchronous and uses the stored object,
  // so every accepted PDF follows the same verification path.
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    if (!documentType || files.length === 0) {
      toast.error("Por favor selecciona un tipo y sube un archivo");
      return false;
    }

    const file = files[0]?.originFileObj;
    if (!file) {
      toast.error("Error al obtener el archivo");
      return false;
    }

    try {
      const uploadSucceeded = await performUpload();
      if (!uploadSucceeded) {
        return false;
      }
      if (activeVault?.id) {
        await invalidateVaultFolder();
      }
      return true;
    } catch (error) {
      console.error("Validation error:", error);
      toast.error("Error al validar el comprobante");
      return false;
    }
  }, [
    activeVault?.id,
    documentType,
    files,
    invalidateVaultFolder,
    performUpload,
  ]);

  // Register submit function with parent so "Siguiente" button can trigger validation
  useEffect(() => {
    if (onRegisterSubmit) {
      onRegisterSubmit(handleSubmit);
    }
  }, [handleSubmit, onRegisterSubmit]);

  // Keep persisted background processing non-blocking. The vault query polls
  // while this requirement stays active; a synchronous validation, if
  // reintroduced, still owns the blocking footer.
  useEffect(() => {
    if (onSetNavigationHidden) {
      onSetNavigationHidden(isValidating && !isProcessing);
    }
    return () => {
      if (onSetNavigationHidden) {
        onSetNavigationHidden(false);
      }
    };
  }, [isProcessing, isValidating, onSetNavigationHidden]);

  // Notify parent when validation flow completes
  useEffect(() => {
    if (onProofOfAddressComplete) {
      onProofOfAddressComplete(isTerminal);
    }
  }, [isTerminal, onProofOfAddressComplete]);

  // Persisted asynchronous processing wins over the local reducer so it never
  // inherits the terminal pending_review presentation.
  if (isProcessing) {
    return (
      <ProofOfAddressProcessingLoader
        documentType={
          documentType
            ? proofOfAddressTypesMap[documentType] || documentType
            : undefined
        }
      />
    );
  }

  // Render validation loader — must be checked before rejected state.
  // During re-upload after rejection, the local reducer sets isValidating=true
  // but activeRequirement.status in Redux is still "invalid" until the API responds.
  // If this check were after the rejected check, the rejected UI would render instead.
  if (isValidating) {
    return (
      <div className="w-full flex items-center justify-center min-h-[60vh]">
        <ProofOfAddressValidationLoader
          progress={validationProgress}
          documentType={
            documentType
              ? proofOfAddressTypesMap[documentType] || documentType
              : undefined
          }
        />
      </div>
    );
  }

  // Render rejected state (show error + upload form)
  if (isRejected || activeRequirement?.status === Status.Invalid) {
    const rejectionReason = invalidReason || activeRequirement?.invalid_reason;

    return (
      <div className="w-full">
        {/* Rejection banner */}
        <div className="mb-6">
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 mb-1">
                Comprobante rechazado
              </h4>
              {rejectionReason && (
                <div className="text-sm text-red-700 mb-3">
                  <InvalidReasonDisplay reason={rejectionReason} />
                </div>
              )}
              <p className="text-xs text-red-600">
                Por favor, vuelve a subir tu comprobante asegurándote de que:
              </p>
              <ul className="mt-2 space-y-1 text-xs text-red-600">
                <li className="flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                  La imagen sea clara y nítida
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                  El documento completo sea visible
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="mt-1 w-1 h-1 rounded-full bg-red-400 flex-shrink-0" />
                  No tenga más de 3 meses de antigüedad
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Upload form (same as default) */}
        {renderUploadForm()}
      </div>
    );
  }

  if (
    activeRequirement?.status === Status.PendingReview ||
    activeRequirement?.status === Status.Valid
  ) {
    return (
      <ProofOfAddressStatus
        status={activeRequirement.status}
        canContinue={canContinue}
      />
    );
  }

  // Render upload form helper
  function renderUploadForm() {
    return (
      <>
        {/* Header */}
        <div className="mb-8">
          <p className="text-gray-600">
            Comprobante de domicilio actual del{" "}
            <span className="font-semibold text-gray-900">{label}</span>, no
            mayor a 3 meses de antigüedad.
          </p>
        </div>

        {/* Show error if any */}
        {hasError && error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Two column layout - stack on mobile, 40/60 split on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-6 md:gap-8">
          {/* Left Column - Document Type Selection */}
          <div className="space-y-6 md:sticky md:top-0 md:self-start">
            <DocumentTypeSelector
              title="Tipo de comprobante"
              options={proofOfAddressTypes}
              value={documentType}
              onChange={handleDocumentTypeChange}
              disabled={!enabledInputs}
            />

            {/* Tips */}
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Requisitos
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                  Documentacion clara y completa
                </li>
                {type === "tenant" && (
                  <li className="flex items-start gap-2">
                    <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                    El domicilio debe coincidir con el formulario
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <span className="mt-1.5 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                  No mayor a 3 meses de antiguedad
                </li>
              </ul>
              <DocumentExamples urls={documentExamples} />
            </div>
          </div>

          {/* Right Column - File Upload */}
          <div className="space-y-6">
            {documentType && (
              <DocumentUploadCard
                name="proof_of_address"
                label={
                  proofOfAddressTypesMap[documentType] ||
                  "Comprobante de domicilio"
                }
                type="both"
                enableCropper={false}
                maxSizeMB={maxFileSizeMB}
                onFileChange={setFiles}
                value={files}
              />
            )}
            <UploadProgress
              status={uploadMutation.status}
              progress={uploadMutation.uploadProgress}
              transport={uploadMutation.uploadTransport}
            />

            {!documentType && enabledInputs && (
              <div className="flex items-center justify-center min-h-[180px] rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                <p className="text-sm text-gray-500">
                  Selecciona un tipo de comprobante para continuar
                </p>
              </div>
            )}

            {/* Cancel to Review - only show for pending review state without OCR */}
            {activeRequirement?.status === Status.PendingReview &&
              !activeRequirement?.verification_data && (
                <CancelToReview requirement={activeRequirement} />
              )}
          </div>
        </div>
      </>
    );
  }

  // Default: render upload form
  return <div className="w-full">{renderUploadForm()}</div>;
};
